import type {
  NotificationItem,
  NotificationScope,
  NotificationType,
  UserType
} from "@/domain/core";

export type UiNotificationType = "info" | "success" | "warning" | "error";

/** Support shares the admin inbox, so it maps onto the "admin" audience. */
export type NotificationAudience = Extract<
  UserType,
  "practice" | "professional" | "admin"
>;

export interface UiNotification {
  id: string;
  title: string;
  message: string;
  type: UiNotificationType;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

interface NotificationPayloadFields {
  message?: string;
  shiftId?: string;
  applicationId?: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined): value is string {
  return typeof value === "string" && UUID_RE.test(value.trim());
}

function parsePayload(data?: string): NotificationPayloadFields {
  if (!data) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(data);
    if (typeof parsed === "string") {
      return { message: parsed };
    }
    if (parsed && typeof parsed === "object") {
      const record = parsed as Record<string, unknown>;
      return {
        message: typeof record.message === "string" ? record.message : undefined,
        shiftId: typeof record.shiftId === "string" ? record.shiftId : undefined,
        applicationId:
          typeof record.applicationId === "string"
            ? record.applicationId
            : undefined
      };
    }
  } catch {
    /* ignore malformed historical payloads */
  }

  return {};
}

function unwrapMessage(raw: string, payload: NotificationPayloadFields): string {
  const trimmed = raw.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    return payload.message ?? raw;
  }

  return raw;
}

function mapNotificationDomainType(type: NotificationType): UiNotificationType {
  switch (type) {
    case "shift_cancelled":
    case "verification_revised":
    case "shift_completion_rejected":
    case "fee_payment_failed":
    case "subscription_payment_failed":
      return "warning";
    case "verification_completed":
    case "professional_selected":
    case "referral_completed":
    case "shift_completion_approved":
    case "payment_confirmed":
      return "success";
    default:
      return "info";
  }
}

function firstUuid(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  return candidates.find(isUuid);
}

export function resolveNotificationActionUrl(
  input: {
    type: NotificationType;
    scope?: NotificationScope;
    scopeId?: string | null;
    shiftId?: string | null;
    applicationId?: string | null;
    data?: string;
  },
  audience: NotificationAudience
): string | undefined {
  const payload = parsePayload(input.data);
  const shiftId = firstUuid(input.shiftId, payload.shiftId);

  switch (input.type) {
    case "shift_post_applied":
      return shiftId && audience === "practice"
        ? `/practice/shifts/${shiftId}/applicants`
        : undefined;
    case "professional_selected":
    case "shift_application":
    case "shift_cancelled":
    case "shift_posted":
    case "shift_post_updated":
      return shiftId && audience === "professional"
        ? `/professional/shifts/${shiftId}`
        : undefined;
    case "verification_completed":
    case "verification_in_progress":
    case "verification_updated":
    case "verification_revised":
      if (audience === "practice") {
        return "/practice/verification";
      }
      if (audience === "professional") {
        return "/professional/verification";
      }
      return undefined;
    case "profile_created":
    case "profile_updated":
      if (audience === "practice") {
        return "/practice/profile";
      }
      if (audience === "professional") {
        return "/professional/profile";
      }
      return undefined;
    // Tempii could not collect its fee; the fix (update the card) lives with
    // billing, but the fee itself is shown on the payments page.
    case "fee_payment_failed":
      return audience === "practice" ? "/practice/payments" : undefined;
    // Same failure, different receivable: an unpaid plan period is listed in the
    // billing history on the subscription page, not with the shift fees.
    case "subscription_payment_failed":
      return audience === "practice" ? "/practice/subscription" : undefined;
    // Both sides get a payment notification, and each has its own page for it:
    // the practice declares from Payments, the professional confirms from Earnings.
    case "payment_declared":
    case "payment_confirmed":
      if (audience === "practice") {
        return "/practice/payments";
      }
      if (audience === "professional") {
        return "/professional/payments";
      }
      return undefined;
    // Attendance and completion claims are addressed to the practice, which
    // approves them from the shift's dates panel.
    case "shift_checked_in":
    case "shift_completion_claimed":
      return shiftId && audience === "practice"
        ? `/practice/shifts/${shiftId}`
        : undefined;
    // The outcome goes back to the professional, on the canonical shift page.
    case "shift_completion_approved":
    case "shift_completion_rejected":
      return shiftId && audience === "professional"
        ? `/professional/shifts/${shiftId}`
        : undefined;
    case "referral_milestone":
    case "referral_completed":
      return audience === "professional" ? "/professional/referrals" : undefined;
    // Ratings received live as a section on the profile page, deliberately not as a
    // nav item of their own.
    case "rating_received":
      if (audience === "practice") {
        return "/practice/profile";
      }
      if (audience === "professional") {
        return "/professional/profile";
      }
      return undefined;
    default:
      return undefined;
  }
}

export function resolveNotificationActionUrlFromPushData(
  data: Record<string, string> | undefined,
  audience: NotificationAudience
): string | undefined {
  if (!data?.type) {
    return undefined;
  }

  return resolveNotificationActionUrl(
    {
      type: data.type as NotificationType,
      scope: data.scope as NotificationScope | undefined,
      scopeId: data.scopeId,
      shiftId: data.shiftId,
      applicationId: data.applicationId
    },
    audience
  );
}

export function mapNotificationItemToUi(
  n: NotificationItem,
  audience: NotificationAudience
): UiNotification {
  const payload = parsePayload(n.data);

  return {
    id: n.notificationId,
    title: n.title,
    message: unwrapMessage(n.message, payload),
    type: mapNotificationDomainType(n.type),
    timestamp: new Date(n.createdAt),
    read: Boolean(n.readAt),
    actionUrl: resolveNotificationActionUrl(
      {
        type: n.type,
        scope: n.scope,
        scopeId: n.scopeId,
        shiftId: n.shiftId,
        applicationId: n.applicationId,
        data: n.data
      },
      audience
    )
  };
}

export function mapNotificationItemsToUi(
  items: NotificationItem[],
  audience: NotificationAudience
): UiNotification[] {
  return items.map((item) => mapNotificationItemToUi(item, audience));
}

export type InboxFilter = "all" | "unread" | "read";
export type InboxSort = "newest" | "oldest" | "title";

export type NotificationBadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline";

export function getNotificationIcon(type: UiNotificationType): string {
  switch (type) {
    case "success":
      return "✅";
    case "warning":
      return "⚠️";
    case "error":
      return "❌";
    default:
      return "ℹ️";
  }
}

export function getNotificationBadgeVariant(
  type: UiNotificationType
): NotificationBadgeVariant {
  switch (type) {
    case "success":
      return "default";
    case "warning":
      return "secondary";
    case "error":
      return "destructive";
    default:
      return "outline";
  }
}

export function formatNotificationTimestamp(
  timestamp: Date,
  now: Date = new Date()
): string {
  const diffInMinutes = Math.floor(
    (now.getTime() - timestamp.getTime()) / (1000 * 60)
  );

  if (diffInMinutes < 1) return "Just now";
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
}

export function toInboxSortParams(sort: InboxSort): {
  sortBy?: string;
  sortOrder: "asc" | "desc";
} {
  if (sort === "title") {
    return { sortBy: "Title", sortOrder: "asc" };
  }

  if (sort === "oldest") {
    return { sortOrder: "asc" };
  }

  return { sortOrder: "desc" };
}
